import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../../../services/api';
import { useNotifier } from '../../../../hooks/useNotifier';
import { triggerFileDownload } from '../utils/formatters';

const sanitizeSegment = (value) =>
  value ? value.toString().trim().replace(/[^a-z0-9-_]+/gi, '_') : 'subcon';

const DownloadSubconDialog = ({ open, projectId, onClose }) => {
  const { notifySuccess, notifyError } = useNotifier();
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/installation-teams');
      const payload = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.installationTeams)
        ? response.data.installationTeams
        : Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data?.items)
        ? response.data.items
        : Array.isArray(response.data?.result)
        ? response.data.result
        : [];
      setOptions(payload.filter((team) => team?.isActive !== false));
    } catch (err) {
      console.error('Error fetching subcon list:', err);
      setOptions([]);
      setError(err.response?.data?.message || 'Gagal memuat daftar subcon. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && options.length === 0 && !loading) {
      fetchOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (downloading) return;
    setSelectedId('');
    setError(null);
    onClose();
  };

  const handleDownload = async () => {
    if (!selectedId) {
      setError('Harap pilih subcon sebelum mengunduh.');
      return;
    }
    const selectedSubcon = options.find((team) => team.id.toString() === selectedId.toString());
    const subconNameSegment = sanitizeSegment(selectedSubcon?.name || 'subcon');
    const dateSegment = new Date().toISOString().split('T')[0];

    try {
      setDownloading(true);
      setError(null);
      const response = await api.get(`/subcon/${selectedId}/opname/download`, {
        params: { projectId },
        responseType: 'blob'
      });
      triggerFileDownload(response.data, `subcon_opname_${subconNameSegment}_${dateSegment}.zip`);
      notifySuccess('Laporan subcon berhasil diunduh');
      handleClose();
    } catch (err) {
      console.error('Error downloading subcon opname:', err);
      notifyError(err.response?.data?.message || 'Gagal mengunduh laporan subcon');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>DOWNLOAD SUBCON OPNAME</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          PILIH SUBCON UNTUK MENGUNDUH LAPORAN PROGRES DAN SELURUH LAMPIRAN TERKAIT.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl fullWidth disabled={loading || downloading} sx={{ mt: 1 }}>
          <InputLabel id="subcon-download-select-label">PILIH SUBCON</InputLabel>
          <Select
            labelId="subcon-download-select-label"
            value={selectedId}
            label="PILIH SUBCON"
            onChange={(event) => {
              setSelectedId(event.target.value);
              if (error) setError(null);
            }}
          >
            {loading && (
              <MenuItem value="">
                <em>MEMUAT DATA SUBCON...</em>
              </MenuItem>
            )}
            {!loading && options.length === 0 && (
              <MenuItem value="" disabled>
                <em>BELUM ADA SUBCON AKTIF</em>
              </MenuItem>
            )}
            {!loading &&
              options.map((team) => (
                <MenuItem key={team.id} value={team.id.toString()}>
                  {team.name}
                </MenuItem>
              ))}
          </Select>
          <FormHelperText>
            {loading ? 'MENGAMBIL DATA SUBCON...' : 'WAJIB PILIH SUBCON SEBELUM MENGUNDUH LAPORAN.'}
          </FormHelperText>
        </FormControl>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            SISTEM AKAN MENGUNDUH FILE EXCEL DAN PAKET ZIP LAMPIRAN.
          </Typography>
          <Button size="small" onClick={fetchOptions} disabled={loading || downloading}>
            Muat ulang
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={downloading}>
          Batal
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={
            downloading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DownloadIcon />
          }
          disabled={downloading || !selectedId || loading}
        >
          {downloading ? 'Mengunduh...' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadSubconDialog;
